import { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Globe,
  Building2,
  Bot,
  Eye,
  Zap,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Loader2,
} from 'lucide-react';

interface FlowStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  detail: string;
}

const steps: FlowStep[] = [
  {
    id: 1,
    title: 'Drop Your URL',
    description: 'Company website or LinkedIn',
    icon: <Globe className="w-5 h-5" />,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/15',
    detail: 'Raisy reads your public presence in seconds — brand voice, value props and key differentiators, from your own entity. The full scan also maps the products, brands and people your page mentions.',
  },
  {
    id: 2,
    title: 'Build Company Profile',
    description: 'AI reads and understands your business',
    icon: <Building2 className="w-5 h-5" />,
    color: 'text-teal-400',
    bgColor: 'bg-teal-500/15',
    detail: 'Generates a structured knowledge graph: products, personas, tone, competitors, ICP.',
  },
  {
    id: 3,
    title: 'Build Processes & Bots',
    description: 'Your workflows and the bots that run them',
    icon: <Bot className="w-5 h-5" />,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/15',
    detail: 'Raisy designs each process — outreach, fundraising, recruiting — and spawns a specialized bot for every step, all pre-trained on your company profile.',
  },
  {
    id: 4,
    title: 'You Review',
    description: 'Every action needs your approval first',
    icon: <Eye className="w-5 h-5" />,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/15',
    detail: 'Dashboard shows every proposed action. One-click approve, edit, or reject. Full audit trail.',
  },
  {
    id: 5,
    title: 'Earn Autonomy',
    description: 'Bots graduate as they prove themselves',
    icon: <Zap className="w-5 h-5" />,
    color: 'text-green-400',
    bgColor: 'bg-green-500/15',
    detail: 'Consistent approvals unlock semi-auto, then full autonomy. Each bot at its own pace.',
  },
];

const simulatedActions = [
  'Scraping raisy.team...',
  'Extracting brand voice...',
  'Building knowledge graph...',
  'Training Fundraiser bot...',
  'Training Sales Leader bot...',
  'Training Recruiter bot...',
  'Ready for review!',
];

export const CompanyBuilderFlow = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [simText, setSimText] = useState('');
  const [showComplete, setShowComplete] = useState(false);

  useEffect(() => {
    if (!isInView) return;
    // Auto-play the flow
    setIsPlaying(true);
    let step = 0;
    const interval = setInterval(() => {
      step++;
      if (step >= steps.length) {
        clearInterval(interval);
        setShowComplete(true);
        return;
      }
      setActiveStep(step);
    }, 1800);
    return () => clearInterval(interval);
  }, [isInView]);

  // Simulation text effect
  useEffect(() => {
    if (!isPlaying || activeStep !== 2) {
      setSimText('');
      return;
    }
    let idx = 0;
    const interval = setInterval(() => {
      if (idx < simulatedActions.length) {
        setSimText(simulatedActions[idx]);
        idx++;
      } else {
        clearInterval(interval);
      }
    }, 400);
    return () => clearInterval(interval);
  }, [isPlaying, activeStep]);

  return (
    <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="text-center mb-16"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 rounded-full text-sm font-medium border dark:border-teal-500/20 mb-6">
          <Sparkles className="w-4 h-4" />
          How Raisy Works
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          From URL to{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500 dark:from-teal-400 dark:to-emerald-400">
            AI Workforce
          </span>{' '}
          in Minutes
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          No configuration. No training data. No complex setup. Just your website URL and Raisy builds your entire workforce.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Steps */}
        <motion.div
          className="lg:col-span-5 space-y-3"
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {steps.map((step, idx) => {
            const isActive = idx === activeStep;
            const isDone = idx < activeStep;
            return (
              <motion.div
                key={step.id}
                className={`relative rounded-xl border p-4 transition-all duration-300
                  ${
                    isActive
                      ? `${step.bgColor} border-teal-500/40 shadow-lg shadow-teal-500/5`
                      : isDone
                      ? 'bg-emerald-50/60 dark:bg-slate-800/40 border-emerald-200 dark:border-emerald-500/20'
                      : 'bg-white dark:bg-slate-800/20 border-slate-200 dark:border-slate-700/30'
                  }`}
                animate={isActive ? { scale: 1.02 } : { scale: 1 }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors
                      ${
                        isActive
                          ? `${step.bgColor} ${step.color}`
                          : isDone
                          ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
                          : 'bg-slate-100 text-slate-400 dark:bg-slate-700/40 dark:text-slate-500'
                      }`}
                  >
                    {isDone ? <CheckCircle2 className="w-5 h-5" /> : step.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-bold ${
                          isActive ? step.color : isDone ? 'text-emerald-400' : 'text-slate-500'
                        }`}
                      >
                        {step.title}
                      </span>
                      {isActive && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center gap-1 text-label-2xs px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-400 font-medium"
                        >
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Active
                        </motion.span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>
                    {(isActive || isDone) && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="text-xs text-slate-600 dark:text-slate-400 mt-2 leading-relaxed"
                      >
                        {step.detail}
                      </motion.p>
                    )}
                  </div>
                </div>

                {/* Connector */}
                {idx < steps.length - 1 && (
                  <div className="absolute -bottom-3 left-7 w-0.5 h-6 bg-gradient-to-b from-slate-300 to-slate-200 dark:from-slate-600/40 dark:to-slate-600/20" />
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Visual Demo */}
        <motion.div
          className="lg:col-span-7 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-xl p-6 backdrop-blur-sm flex flex-col"
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Live Preview</h3>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-400">Auto-playing</span>
            </div>
          </div>

          {/* Mock Browser */}
          <div className="flex-1 bg-slate-100 dark:bg-slate-950 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              </div>
              <div className="flex-1 mx-2">
                <div className="bg-slate-100 dark:bg-slate-800 rounded px-3 py-1 text-xs text-slate-500 flex items-center gap-2">
                  <Globe className="w-3 h-3" />
                  {activeStep === 0
                    ? 'Enter company URL...'
                    : activeStep === 1
                    ? 'raisy.team'
                    : 'raisy.team — Company Profile'}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 relative">
              <AnimatePresence mode="wait">
                {activeStep === 0 && (
                  <motion.div
                    key="step0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center h-full space-y-4"
                  >
                    <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center">
                      <Globe className="w-8 h-8 text-teal-400" />
                    </div>
                    <div className="text-center">
                      <div className="text-slate-900 dark:text-white font-medium">Enter your company URL</div>
                      <div className="text-sm text-slate-500 dark:text-slate-500">Raisy will read and understand your business</div>
                    </div>
                    <div className="w-full max-w-xs h-9 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center px-3 text-xs text-slate-500 dark:text-slate-400">
                      https://yourcompany.com
                    </div>
                  </motion.div>
                )}

                {activeStep === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <div className="text-sm font-medium text-slate-900 dark:text-white mb-3">Company Profile Generated</div>
                    {['Brand Voice', 'Value Propositions', 'Target Personas', 'Competitors', 'Key Differentiators'].map(
                      (item, i) => (
                        <motion.div
                          key={item}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.15 }}
                          className="flex items-center gap-2 text-sm"
                        >
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                          <span className="text-slate-600 dark:text-slate-300">{item}</span>
                          <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${85 + i * 3}%` }}
                              transition={{ delay: i * 0.15 + 0.2, duration: 0.5 }}
                            />
                          </div>
                        </motion.div>
                      )
                    )}
                  </motion.div>
                )}

                {activeStep === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Loader2 className="w-4 h-4 text-teal-400 animate-spin" />
                      <span className="text-sm font-medium text-teal-400">{simText || 'Initializing bots...'}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {['Fundraiser', 'Sales Leader', 'Recruiter', 'IR Manager', 'CRM Admin', 'Content Writer', 'Data Analyst', 'Customer Success'].map(
                        (role, i) => (
                          <motion.div
                            key={role}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center gap-2 px-3 py-2 rounded bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/40 text-xs"
                          >
                            <Bot className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                            <span className="text-slate-600 dark:text-slate-300">{role}</span>
                          </motion.div>
                        )
                      )}
                    </div>
                  </motion.div>
                )}

                {activeStep >= 3 && (
                  <motion.div
                    key="step3plus"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">Approval Dashboard</span>
                      <span className="text-xs text-teal-600 dark:text-teal-400">
                        {activeStep === 4 ? '89% autonomy rate' : 'All actions require approval'}
                      </span>
                    </div>
                    {[
                      { action: 'Draft email to investor', status: 'approved' as const, bot: 'Fundraiser' },
                      { action: 'Score incoming lead', status: activeStep >= 4 ? 'approved' : 'pending' as const, bot: 'Sales Leader' },
                      { action: 'Schedule candidate interview', status: activeStep >= 4 ? 'auto' : 'pending' as const, bot: 'Recruiter' },
                    ].map((item, i) => (
                      <motion.div
                        key={item.action}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.15 }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/30"
                      >
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center
                            ${
                              item.status === 'approved'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : item.status === 'auto'
                                ? 'bg-teal-500/20 text-teal-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }`}
                        >
                          {item.status === 'approved' || item.status === 'auto' ? (
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-slate-600 dark:text-slate-300">{item.action}</div>
                          <div className="text-label-2xs text-slate-500">{item.bot}</div>
                        </div>
                        <span
                          className={`text-label-2xs px-2 py-0.5 rounded-full font-medium
                            ${
                              item.status === 'approved'
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : item.status === 'auto'
                                ? 'bg-teal-500/10 text-teal-400'
                                : 'bg-amber-500/10 text-amber-400'
                            }`}
                        >
                          {item.status === 'auto' ? 'Auto' : item.status}
                        </span>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Completion overlay */}
              <AnimatePresence>
                {showComplete && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 bg-white/90 dark:bg-slate-950/90 flex flex-col items-center justify-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', damping: 12 }}
                      className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4"
                    >
                      <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                    </motion.div>
                    <div className="text-slate-900 dark:text-white font-bold text-lg">Your AI Workforce is Ready</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">Processes built • hundreds of bots • Full audit trail</div>
                    <div className="flex items-center gap-2 mt-4 text-xs text-teal-600 dark:text-teal-400">
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Time to first value: under 10 minutes</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
